// The name / entity / COA-template fields shared by first-run onboarding and
// the "Add business" modal in settings. Extracted so the two entry points can
// never drift on entity types or template copy.

import React from 'react';
import { XStack, YStack } from 'tamagui';
import { Check } from 'lucide-react';
import { GBCard, GBInput, GBSelect, GBText } from '@greenbackapp/ui';
import { useSemanticColors } from './semanticColors';
import { BusinessEntityType, CoaTemplate } from '../../../types/businessApi';
import { DEFAULT_FRAMEWORK } from './reportingFramework';

export const ENTITY_OPTIONS: { value: BusinessEntityType; label: string }[] = [
  { value: 'sole_prop', label: 'Sole proprietorship' },
  { value: 'llc', label: 'LLC' },
  { value: 's_corp', label: 'S corporation' },
  { value: 'c_corp', label: 'C corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'rental_holding', label: 'Rental holding' },
  // PRD 83 §3.2. Available only under their own reporting framework — the api
  // refuses the pair otherwise, so a picker that offered these to every
  // business would be offering a 422.
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'government', label: 'Government' },
];

/**
 * What a brand-new business may be: the default framework's entity types.
 * Derived rather than re-listed so adding a framework cannot silently widen
 * the create form (PRD 83 §3.2).
 */
export const CREATE_ENTITY_OPTIONS = ENTITY_OPTIONS.filter(option =>
  DEFAULT_FRAMEWORK.entity_types.includes(option.value),
);

export const TEMPLATES: { value: CoaTemplate; title: string; description: string }[] = [
  {
    value: 'general_business',
    title: 'General business',
    description:
      'A standard chart of accounts for service or product businesses — sales, payroll, operating expenses.',
  },
  {
    value: 'rental_property',
    title: 'Rental property',
    description:
      'Property-centric accounts for landlords — rental income, repairs, and per-property tracking.',
  },
  {
    value: 'solopreneur',
    title: 'Solopreneur',
    description: 'A lean chart of accounts for freelancers and single-owner businesses.',
  },
];

export interface BusinessCreateFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
  entityType: BusinessEntityType | '';
  onEntityTypeChange: (value: BusinessEntityType | '') => void;
  template: CoaTemplate;
  onTemplateChange: (value: CoaTemplate) => void;
  disabled?: boolean;
}

export const BusinessCreateFields: React.FC<BusinessCreateFieldsProps> = ({
  name,
  onNameChange,
  entityType,
  onEntityTypeChange,
  template,
  onTemplateChange,
  disabled = false,
}) => {
  const semantic = useSemanticColors();

  return (
    <>
      <YStack gap={16}>
        <GBInput
          label="Business name"
          placeholder="e.g. Main Street Consulting LLC"
          value={name}
          onChangeText={onNameChange}
          disabled={disabled}
          required
        />

        <GBSelect
          label="Entity type (optional)"
          placeholder="Select entity type"
          value={entityType}
          onChange={value => onEntityTypeChange(value as BusinessEntityType)}
          // A business is created on the default framework, so only its entity
          // types are offered here. Nonprofit and government are chosen by
          // switching the reporting framework in Business profile, where the
          // two are set together (PRD 83 §3.5).
          options={CREATE_ENTITY_OPTIONS}
          disabled={disabled}
        />
      </YStack>

      <YStack gap={12}>
        <GBText variant="bodyMedium" bold>
          Chart of accounts template
        </GBText>
        <GBText variant="bodySmall" color="secondary">
          We'll seed your books with a starter chart of accounts. You can add or rename accounts
          later.
        </GBText>
        <YStack gap={12}>
          {TEMPLATES.map(option => {
            const selected = template === option.value;
            return (
              <GBCard
                key={option.value}
                padding={16}
                pressable
                onPress={() => (disabled ? undefined : onTemplateChange(option.value))}
                borderWidth={selected ? 2 : 1}
                borderColor={selected ? semantic.positive : undefined}
              >
                <XStack gap={12} alignItems="center">
                  <YStack flex={1} gap={4}>
                    <GBText variant="bodyMedium" bold>
                      {option.title}
                    </GBText>
                    <GBText variant="bodySmall" color="secondary">
                      {option.description}
                    </GBText>
                  </YStack>
                  {selected && <Check size={20} color={semantic.positive} />}
                </XStack>
              </GBCard>
            );
          })}
        </YStack>
      </YStack>
    </>
  );
};

export default BusinessCreateFields;
